import {
  getBinanceTicker24h,
  getBingxTicker24h,
  getBitgetTicker,
  getBitmartTicker,
  getBybitTicker,
  getCoinstoreTickers,
  getGateTickers,
  getHotcoinTicker,
  getHtxTicker,
  getIndodaxTickers,
  getKrakenTicker,
  getKucoinStats24h,
  getLbankBookTicker,
  getLbankTicker24h,
  getMexcTicker24h,
  getOkxTicker,
  getWhitebitTickerV1,
  getXtTicker,
  proxyRestOptions,
} from '@/shared/cex-sdk';
import { formatWeexSymbol, getWeexTicker } from '@/shared/cex-sdk/exchanges/weex/rest';
import { formatBiconomySymbol, getBiconomyTickers } from '@/shared/cex-sdk/exchanges/biconomy/rest';

import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';
import { ECexName } from 'web3/cexs';

import { calculateSpreadPercentage } from '../lib/spread-calculation';
import { ITickerApiResponse } from '../model';

import { GetCexTickerRequest, GetCexTickerResponse } from './api.types';

/**
 * Symbol format helpers for different exchanges
 */
const formatSymbol = {
  /** Remove slash for exchanges like MEXC, Binance (BTC/USDT -> BTCUSDT) */
  noSlash: (symbol: string) => symbol.replace('/', '').replace('-', ''),
  /** Uppercase with underscore for exchanges like Gate, Bitmart (BTC/USDT -> BTC_USDT) */
  underscore: (symbol: string) => symbol.replace('/', '_').replace('-', '_'),
  /** Lowercase with underscore for exchanges like LBank, XT (BTC/USDT -> btc_usdt) */
  lowerUnderscore: (symbol: string) => symbol.replace('/', '_').replace('-', '_').toLowerCase(),
  /** Hyphen separator for exchanges like OKX, BingX (BTC/USDT -> BTC-USDT) */
  hyphen: (symbol: string) => symbol.replace('/', '-'),
  /** Lowercase concatenation for HTX (BTC/USDT -> btcusdt) */
  lowerNoSlash: (symbol: string) => symbol.replace('/', '').replace('-', '').toLowerCase(),
};

/**
 * Calculate 24h price change percentage from open and last price
 */
const calculatePriceChange = (last_price: string, openPrice: string): string => {
  const last = parseFloat(last_price);
  const open = parseFloat(openPrice);
  if (open === 0) return '0';
  return (((last - open) / open) * 100).toFixed(2);
};

/**
 * Fallback ticker fetchers for each exchange.
 * Each function fetches ticker data directly from the exchange API and maps it to ITickerData.
 */
const fallbackFetchers: Record<
  ECexName,
  (req: GetCexTickerRequest) => Promise<GetCexTickerResponse>
> = {
  // +
  [ECexName.mexc]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getMexcTicker24h({ symbol }, proxyRestOptions);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: response.lastPrice,
        change24h: response.priceChangePercent,
        high24h: response.highPrice,
        low24h: response.lowPrice,
        base_volume: response.volume,
        quote_volume: response.quoteVolume,
        spread_percentage: calculateSpreadPercentage(response.askPrice, response.bidPrice) || '0',
      },
    });
  },

  // +
  [ECexName.binance]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getBinanceTicker24h({ symbol }, proxyRestOptions);
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: response.lastPrice,
        change24h: response.priceChangePercent,
        high24h: response.highPrice,
        low24h: response.lowPrice,
        base_volume: response.volume,
        quote_volume: response.quoteVolume,
        spread_percentage: calculateSpreadPercentage(response.askPrice, response.bidPrice) || '0',
      },
    });
  },

  // +
  [ECexName.gate]: async req => {
    const currency_pair = formatSymbol.underscore(req.symbol);
    const response = await getGateTickers({ currency_pair }, proxyRestOptions);

    if (!response[0]) {
      throw new Error('No ticker data returned from Gate.io');
    }

    const ticker = response[0];
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h: ticker.change_percentage,
        high24h: ticker.high_24h,
        low24h: ticker.low_24h,
        base_volume: ticker.base_volume,
        quote_volume: ticker.quote_volume,
        spread_percentage: calculateSpreadPercentage(ticker.lowest_ask, ticker.highest_bid) || '0',
      },
    });
  },

  // +
  [ECexName.kucoin]: async req => {
    const symbol = formatSymbol.hyphen(req.symbol);
    const response = await getKucoinStats24h({ symbol }, proxyRestOptions);

    if (!response.data) {
      throw new Error('No ticker data returned from KuCoin');
    }

    const ticker = response.data;
    // KuCoin changeRate is decimal (0.0015 = 0.15%), convert to percentage
    const changePercent = (parseFloat(ticker.changeRate) * 100).toFixed(2);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h: changePercent,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: ticker.volValue,
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // +
  [ECexName.bybit]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getBybitTicker({ category: 'spot', symbol }, proxyRestOptions);

    if (!response.result?.list?.[0]) {
      throw new Error('No ticker data returned from Bybit');
    }

    const ticker = response.result.list[0];
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPrice,
        change24h: ticker.price24hPcnt,
        high24h: ticker.highPrice24h,
        low24h: ticker.lowPrice24h,
        base_volume: ticker.volume24h,
        quote_volume: ticker.turnover24h,
        spread_percentage: calculateSpreadPercentage(ticker.ask1Price, ticker.bid1Price) || '0',
      },
    });
  },

  // +
  [ECexName.htx]: async req => {
    const symbol = formatSymbol.lowerNoSlash(req.symbol);
    const response = await getHtxTicker({ symbol }, proxyRestOptions);

    if (!response.tick) {
      throw new Error('No ticker data returned from HTX');
    }

    const ticker = response.tick;
    const change24h = calculatePriceChange(ticker.close.toString(), ticker.open.toString());

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.close.toString(),
        change24h,
        high24h: ticker.high.toString(),
        low24h: ticker.low.toString(),
        base_volume: ticker.amount.toString(),
        quote_volume: ticker.vol.toString(),
        spread_percentage:
          calculateSpreadPercentage(ticker.ask.toString(), ticker.bid.toString()) || '0',
      },
    });
  },

  // +
  [ECexName.okx]: async req => {
    const instId = formatSymbol.hyphen(req.symbol);
    const response = await getOkxTicker({ instId }, proxyRestOptions);

    if (!response.data?.[0]) {
      throw new Error('No ticker data returned from OKX');
    }

    const ticker = response.data[0];
    const change24h = calculatePriceChange(ticker.last, ticker.open24h);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h,
        high24h: ticker.high24h,
        low24h: ticker.low24h,
        base_volume: ticker.vol24h,
        quote_volume: ticker.volCcy24h,
        spread_percentage: calculateSpreadPercentage(ticker.askPx, ticker.bidPx) || '0',
      },
    });
  },

  // +
  [ECexName.weex]: async req => {
    // WEEX uses the legacy "_SPBL" symbol suffix and returns ticker `data` as an object.
    const [base, quote] = req.symbol.split(/[/-]/);
    const symbol = formatWeexSymbol(base ?? '', quote ?? '');
    const response = await getWeexTicker({ symbol }, proxyRestOptions);

    if (response.code !== '00000' || !response.data) {
      throw new Error('No ticker data returned from WEEX');
    }

    const ticker = response.data;
    const change24h =
      ticker.priceChangePercent ?? calculatePriceChange(ticker.lastPrice, ticker.open);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPrice,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.size,
        quote_volume: ticker.value,
        spread_percentage: '0',
      },
    });
  },

  // +
  [ECexName.biconomy]: async req => {
    // Biconomy has no per-symbol ticker endpoint — fetch all tickers and filter by symbol.
    const [base, quote] = req.symbol.split(/[/-]/);
    const symbol = formatBiconomySymbol(base ?? '', quote ?? '');
    const response = await getBiconomyTickers(proxyRestOptions);

    const ticker = response.ticker?.find(t => t.symbol?.toUpperCase() === symbol);
    if (!ticker) {
      throw new Error('No ticker data returned from Biconomy');
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        // Biconomy `change` is already a 24h percentage.
        change24h: ticker.change,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: ticker.deal,
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // +
  [ECexName.bitget]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getBitgetTicker({ symbol }, proxyRestOptions);

    if (!response.data?.[0]) {
      throw new Error('No ticker data returned from Bitget');
    }

    const ticker = response.data[0];
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPr,
        change24h: ticker.change24h,
        high24h: ticker.high24h,
        low24h: ticker.low24h,
        base_volume: ticker.baseVolume,
        quote_volume: ticker.quoteVolume,
        spread_percentage:
          calculateSpreadPercentage(
            ticker.sellOne as string | number,
            ticker.buyOne as string | number,
          ) || '0',
      },
    });
  },

  // +
  [ECexName.bingx]: async req => {
    const symbol = formatSymbol.hyphen(req.symbol);
    const response = await getBingxTicker24h({ symbol }, proxyRestOptions);
    const ticker = response.data;

    if (!ticker) {
      throw new Error('No ticker data returned from BingX');
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.lastPrice.toString(),
        change24h: ticker.priceChange.toString(),
        high24h: ticker.highPrice.toString(),
        low24h: ticker.lowPrice.toString(),
        base_volume: ticker.volume.toString(),
        quote_volume: ticker.quoteVolume.toString(),
        spread_percentage: '0',
      },
    });
  },

  // +
  [ECexName.bitmart]: async req => {
    const symbol = formatSymbol.underscore(req.symbol);
    const response = await getBitmartTicker({ symbol }, proxyRestOptions);

    if (!response.data) {
      throw new Error('No ticker data returned from Bitmart');
    }

    const ticker = response.data;
    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last_price,
        change24h: ticker.fluctuation,
        high24h: ticker.high_24h,
        low24h: ticker.low_24h,
        base_volume: ticker.base_volume_24h,
        quote_volume: ticker.quote_volume_24h,
        spread_percentage: calculateSpreadPercentage(ticker.best_ask, ticker.best_bid) || '0',
      },
    });
  },

  // +
  [ECexName.xt]: async req => {
    const symbol = formatSymbol.lowerUnderscore(req.symbol);
    const response = await getXtTicker({ symbol }, proxyRestOptions);

    const ticker = response?.result?.[0];
    if (!ticker) {
      throw new Error('No ticker data returned from XT');
    }

    const change24h = calculatePriceChange(ticker.c, ticker.o);
    // Alternatively use ticker.cr if it's already a percentage string
    // const change24h = (parseFloat(ticker.cr) * 100).toFixed(2);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.c,
        change24h,
        high24h: ticker.h,
        low24h: ticker.l,
        base_volume: ticker.v,
        quote_volume: ticker.q,
        spread_percentage: calculateSpreadPercentage(ticker.ap, ticker.bp) || '0',
      },
    });
  },

  // +
  [ECexName.lbank]: async req => {
    const symbol = formatSymbol.lowerUnderscore(req.symbol);

    // Fetch both ticker data and book ticker for complete information
    const [tickerResponse, bookTickerResponse] = await Promise.all([
      getLbankTicker24h({ symbol }, proxyRestOptions),
      getLbankBookTicker({ symbol }, proxyRestOptions),
    ]);

    const ticker = tickerResponse?.data?.[0]?.ticker;
    const bookTicker = bookTickerResponse?.data;

    if (!ticker) {
      throw new Error('No ticker data returned from LBank');
    }

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.latest,
        change24h: ticker.change,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: ticker.turnover,
        spread_percentage: bookTicker
          ? calculateSpreadPercentage(bookTicker.askPrice, bookTicker.bidPrice) || '0'
          : '0',
      },
    });
  },

  // +
  [ECexName.whitebit]: async req => {
    const market = formatSymbol.underscore(req.symbol);
    const response = await getWhitebitTickerV1({ market }, proxyRestOptions);

    if (!response.result) {
      throw new Error(`No ticker data returned from WhiteBIT for symbol ${market}`);
    }

    const ticker = response.result;

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h: ticker.change,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.volume,
        quote_volume: ticker.deal,
        spread_percentage: calculateSpreadPercentage(ticker.ask, ticker.bid) || '0',
      },
    });
  },

  // +
  [ECexName.coinstore]: async req => {
    const symbol = formatSymbol.noSlash(req.symbol);
    const response = await getCoinstoreTickers(proxyRestOptions);

    if (!response.data || !Array.isArray(response.data)) {
      throw new Error('No ticker data returned from Coinstore');
    }

    const ticker = response.data.find(t => t.symbol === symbol);

    if (!ticker) {
      throw new Error(`Ticker not found for symbol ${symbol} in Coinstore`);
    }

    const change24h = calculatePriceChange(ticker.close, ticker.open);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.close,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.volume,
        quote_volume: ticker.amount,
        spread_percentage: calculateSpreadPercentage(ticker.ask, ticker.bid) || '0',
      },
    });
  },

  // +
  [ECexName.indodax]: async req => {
    const symbol = formatSymbol.lowerUnderscore(req.symbol);
    const response = await getIndodaxTickers(proxyRestOptions);

    if (!response.tickers || !response.tickers[symbol]) {
      throw new Error(`Ticker not found for symbol ${symbol} in Indodax`);
    }

    const ticker = response.tickers[symbol];

    // Indodax uses dynamic volume keys (e.g., vol_btc for base, vol_idr for quote for btc_idr pair)
    const [base, quote] = symbol.split('_');
    const baseVolume = ticker[`vol_${base}`]?.toString() || '0';
    const quoteVolume = ticker[`vol_${quote}`]?.toString() || '0';

    // Calculate change based on high and low if not provided (Indodax API doesn't guarantee change field)
    // Actually, Indodax doesn't provide open price in this endpoint, so we can't calculate change24h accurately
    // We'll use 0 or estimate if possible. But wait, ticker response has high/low/last/buy/sell.
    // Let's rely on fallback calculation or just high/low difference which is spread, not change.
    // For now returning '0' for change as it's missing in Indodax public ticker.
    const change24h = '0';

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: baseVolume,
        quote_volume: quoteVolume,
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // -
  [ECexName.hotcoin]: async req => {
    const symbol = formatSymbol.lowerNoSlash(req.symbol);
    const response = await getHotcoinTicker({ symbol });

    if (!response.data) {
      throw new Error('No ticker data returned from Hotcoin');
    }

    const ticker = response.data;
    // Hotcoin doesn't provide quote volume or change percentage directly
    const change24h = '0'; // Would need open price to calculate this

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: ticker.last,
        change24h,
        high24h: ticker.high,
        low24h: ticker.low,
        base_volume: ticker.vol,
        quote_volume: '0', // Not provided by Hotcoin API
        spread_percentage: calculateSpreadPercentage(ticker.sell, ticker.buy) || '0',
      },
    });
  },

  // +
  [ECexName.kraken]: async req => {
    // Kraken uses different pair naming (e.g., XBTUSD for BTC/USD)
    // This is a simplified version - may need pair name mapping
    const pair = formatSymbol.noSlash(req.symbol);
    const response = await getKrakenTicker({ pair }, proxyRestOptions);

    const pairData = response.result?.[pair] || response.result?.[Object.keys(response.result)[0]];
    if (!pairData) {
      throw new Error('No ticker data returned from Kraken');
    }

    // Kraken returns arrays: c[0] = last price, h[1] = 24h high, l[1] = 24h low, v[1] = 24h volume, o = open
    const lastPrice = pairData.c[0];
    const change24h = calculatePriceChange(lastPrice, pairData.o);

    return mapResponse<ITickerApiResponse>({
      data: {
        last_price: lastPrice,
        change24h,
        high24h: pairData.h[1],
        low24h: pairData.l[1],
        base_volume: pairData.v[1],
        quote_volume: (parseFloat(pairData.v[1]) * parseFloat(pairData.p[1])).toString(), // Estimated
        spread_percentage: calculateSpreadPercentage(pairData.a[0], pairData.b[0]) || '0',
      },
    });
  },
  [ECexName.coinw]: async () => {
    throw new Error('No fallback fetcher implemented for CoinW');
  },
};

/**
 * Fallback function to get ticker data directly from exchange APIs
 * when the main API fails.
 */
export default async function getCexTickerFallback(
  req: GetCexTickerRequest,
): Promise<GetCexTickerResponse> {
  try {
    const fetcher = fallbackFetchers[req.cex];

    if (!fetcher) {
      throw new Error(`No fallback fetcher implemented for ${req.cex}`);
    }
    return fetcher(req);
  } catch (error) {
    return mapError<ITickerApiResponse>({ error });
  }
}
